import { parseISO, startOfWeek } from "date-fns";
import type { HomePhase, PhaseOverride } from "@/components/v2/home/types";
import type { Poll } from "@/lib/api/polls";
import type { PollV2 } from "@/lib/api/polls";
import type { MatchEvent, CompletedMatchContext } from "@/lib/api/rsvp";
import { isEventPostKickoff } from "@/lib/v2/eventPhase";

const COMPLETED_RESET_MS = 48 * 60 * 60 * 1000;

function isEventInCurrentWeek(eventDateIso: string): boolean {
  const weekStart = startOfWeek(new Date(), { weekStartsOn: 0 });
  return parseISO(eventDateIso).getTime() >= weekStart.getTime();
}

export interface DeriveHomePhaseInput {
  phaseOverride: PhaseOverride;
  activePoll: Poll | PollV2 | null;
  upcomingEvent: MatchEvent | null;
  lastCompleted: CompletedMatchContext | null;
}

/** Client-derived home phase from poll + event state. */
export function deriveHomePhase({
  phaseOverride,
  activePoll,
  upcomingEvent,
  lastCompleted,
}: DeriveHomePhaseInput): HomePhase {
  if (phaseOverride !== "auto") {
    return phaseOverride;
  }

  if (activePoll) {
    return "poll_open";
  }

  if (upcomingEvent && isEventInCurrentWeek(upcomingEvent.event_date)) {
    if (isEventPostKickoff(upcomingEvent)) {
      return "post_kickoff";
    }
    return "convocatoria";
  }

  const isCompletedStale =
    lastCompleted != null &&
    Date.now() - parseISO(lastCompleted.event.event_date).getTime() >= COMPLETED_RESET_MS;

  if (lastCompleted && !isCompletedStale) {
    return "completed";
  }

  return "poll_inactive";
}

export { COMPLETED_RESET_MS };
